import React, { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";

import { Input } from "components/ui/input";
import { useBreadcrumbs } from "app/breadcrumb-provider";
import { Combobox } from "components/ui/combobox";
import { useAuth } from "app/provider";
import { getCustomerReports } from "../settings/customers/api/customer";
import ReportsColumn from "./ReportsColumn";
import { Report } from "types/settings";

const REPORT_GRID =
  "grid-cols-[minmax(260px,1.4fr)_minmax(160px,1fr)_300px_150px]";

const STORAGE_KEY = "selectedCustId";

const Reports = () => {
  const { userType, customers } = useAuth();
  const { setBreadcrumbs, setHeaderBorder } = useBreadcrumbs();

  const [search, setSearch] = useState("");
  const [selectedCustId, setSelectedCustId] = useState<string>();
  const [reports, setReports] = useState<Report[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  useEffect(() => {
    setBreadcrumbs([{ label: "Reports", href: "/reports" }]);
    setHeaderBorder(false);

    return () => setHeaderBorder(true);
  }, [setBreadcrumbs, setHeaderBorder]);

  useEffect(() => {
    const fetchReports = async () => {
      if (!selectedCustId) {
        setReports([]);
        return;
      }

      try {
        setReportsLoading(true);
        const response = await getCustomerReports(selectedCustId);
        setReports(response || []);
      } catch (err) {
        console.error("Error fetching reports:", err);
        setReports([]);
      } finally {
        setReportsLoading(false);
      }
    };

    fetchReports();
  }, [selectedCustId]);

  useEffect(() => {
    if (!customers?.length) {
      setSelectedCustId(undefined);
      return;
    }

    if (userType === "Internal") {
      const storedCustId = localStorage.getItem(STORAGE_KEY);

      const isStoredValid = storedCustId
        ? customers.some(c => c.cust_code === storedCustId)
        : false;

      const defaultCustId = isStoredValid
        ? storedCustId!
        : customers[0].cust_code;

      setSelectedCustId(defaultCustId);

      localStorage.setItem(STORAGE_KEY, defaultCustId);

      return;
    }

    setSelectedCustId(customers[0].cust_code);
  }, [userType, customers]);

  const customerOptions =
    customers?.map(c => ({
      value: c.cust_code,
      label: c.cust_name,
    })) || [];

  const filteredReports = useMemo(() => {
    const q = search.trim().toLowerCase();

    const mappedReports = reports.map(report => ({...report, title: report.report_name }));

    if (!q) return mappedReports;

    return mappedReports.filter(
      report =>
        report.title.toLowerCase().includes(q)
    );
  }, [reports, search]);

  const handleCustomerChange = (custId: string) => {
    const nextCustId = custId || undefined;

    setSelectedCustId(nextCustId);

    if (nextCustId) {
      localStorage.setItem(STORAGE_KEY, nextCustId);
    }
  };

  return (
    <div className="min-h-full bg-white">
      <div className="relative overflow-hidden py-8">
        <div className="absolute inset-0 pointer-events-none opacity-40 bg-[radial-gradient(circle_at_20px_20px,#dbeafe_1px,transparent_1px)] [background-size:42px_42px]" />

        <div className="relative w-full px-6 lg:px-10 xl:px-16">
          <h1 className="text-center text-2xl font-semibold text-slate-900">
            Reports Center
          </h1>

          <div className="mt-4 flex items-center justify-center">
            <div className="flex flex-1 items-center justify-center space-x-2 pt-1">
              <div className="relative w-full max-w-xl">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />

                <Input
                  placeholder="Search Reports"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-8 w-full pl-8 text-xs font-normal text-gray-600 placeholder:text-gray-500"
                />
              </div>
              {userType === "Internal" && (
                <div className="h-8 w-[220px] flex-shrink-0">
                  <Combobox
                    options={customerOptions}
                    value={selectedCustId || ""}
                    onValueChange={handleCustomerChange}
                    placeholder="All Customers"
                    searchPlaceholder="Search customers..."
                    emptyText="No customer found."
                    buttonClassName="h-8 text-xs font-normal text-gray-600"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-6 lg:px-10 xl:px-16">
        <div className="w-full overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="min-w-[1150px]">
            <div className="px-4 py-3">
              <h2 className="text-lg font-semibold text-slate-900">
                All Reports
              </h2>
            </div>

            <div
              className={`grid ${REPORT_GRID} border-y border-slate-200 bg-slate-50 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500`}
            >
              <div>Report Name</div>
              <div>Created By</div>
              <div>Date Range</div>
              <div>Action</div>
            </div>

            {filteredReports.length > 0 ? (
              filteredReports.map(report => (
                <div
                  key={report.report_id}
                  className={`grid ${REPORT_GRID} items-center gap-3 border-b border-slate-200 px-4 py-3 text-left text-sm last:border-b-0`}
                >
                  <div className="font-medium text-black">
                    {report.title}
                  </div>

                  <div className="text-slate-700">
                    System Generated
                  </div>

                  <ReportsColumn
                    endpoint={report.endpoint_slug || ""}
                    fileName={report.report_name}
                    selectedCustId={selectedCustId}
                  />
                </div>
              ))
            ) : (
              <div className="py-10 text-center text-sm text-slate-500">
                {reportsLoading ? "Loading reports..." : "No reports found."}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;